// ============================================================================
// DefaultCompositor — Shared layer compositing for HostBridge + Orchestrator
// ============================================================================
// Extracted from the nearly-identical rendering code in:
//   - host-bridge.ts → renderSingleThread()
//   - orchestrator-worker.ts → renderComposite()
//
// Uses RenderCache for 80× faster pattern-based checkerboard (instead of
// per-pixel fillRect loops), and provides dirty-aware layer canvas caching.
//
// Usage:
//   const compositor = new DefaultCompositor();
//   compositor.render(ctx2d, canvas.width, canvas.height, descriptorTable, frameSABs);
import { BufferFormat, BufferFlags } from '../types.js';
import { RenderCache } from './render-cache.js';
export class DefaultCompositor {
    renderCache = new RenderCache();
    layerCanvasCache = new Map();
    /**
     * Composite all layers from the descriptor table onto the canvas context.
     * Draws checkerboard background, then composites each COMPOSITE-flagged layer
     * bottom-to-top with dirty-aware caching.
     *
     * @param ctx - 2D rendering context to draw into
     * @param width - canvas width
     * @param height - canvas height
     * @param layers - composite layers sorted by layerOrder
     * @param getBuffer - function to retrieve SAB data for a buffer ID
     */
    render(ctx, width, height, layers, getBuffer) {
        // Draw checkerboard background (pattern-based, GPU-accelerated)
        const bg = this.renderCache.getCheckerboard(width, height);
        ctx.drawImage(bg, 0, 0);
        // Composite layers bottom-to-top — dirty-aware
        for (const layer of layers) {
            const sab = getBuffer(layer.bufferId);
            if (!sab)
                continue;
            if (layer.format === BufferFormat.RGBA8) {
                try {
                    const isDirty = (layer.flags & BufferFlags.DIRTY) !== 0;
                    const { canvas: layerCanvas, ctx: layerCtx } = this.getLayerCanvas(layer.bufferId, layer.width, layer.height);
                    if (isDirty) {
                        // Layer mutated — copy pixels from SAB into cached canvas.
                        // ImageData requires plain ArrayBuffer — SAB is rejected.
                        const byteCount = layer.width * layer.height * 4;
                        const plain = new Uint8ClampedArray(byteCount);
                        plain.set(new Uint8ClampedArray(sab, 0, byteCount));
                        const imgData = new ImageData(plain, layer.width, layer.height);
                        layerCtx.putImageData(imgData, 0, 0);
                    }
                    // Always composite — dirty or not, layer is still visible
                    ctx.drawImage(layerCanvas, 0, 0);
                }
                catch {
                    // size mismatch — skip
                }
            }
        }
    }
    /**
     * Get or create a reusable OffscreenCanvas for a given layer.
     * Avoids allocating a new OffscreenCanvas + context every frame per layer.
     */
    getLayerCanvas(bufferId, w, h) {
        const cached = this.layerCanvasCache.get(bufferId);
        if (cached && cached.w === w && cached.h === h)
            return cached;
        const c = new OffscreenCanvas(w, h);
        const ctx = c.getContext('2d');
        const entry = { canvas: c, ctx, w, h };
        this.layerCanvasCache.set(bufferId, entry);
        return entry;
    }
    /** Release all cached canvases and resources. */
    destroy() {
        this.layerCanvasCache.clear();
        this.renderCache.clear();
    }
}
//# sourceMappingURL=default-compositor.js.map